import { useEffect, useState } from 'react'
import { selectCustomerNumber, selectTeamMembers, selectCustomer, selectGroups, getCustomerMetadata } from '../../../redux/customer'
import { selectCurrentUser, selectIsAdminOrOwner } from '../../../redux/user'
import { useAppDispatch, useAppSelector } from '../../../redux/store'
import { Box, Card, useTheme, styled, Typography, FormGroup, Checkbox, FormControlLabel, Button, Chip } from '@mui/material'
import PageHeader from '../../../components/PageHeader'
import { CustomerDetails, GroupName, LoadingState, User } from '../../../types'
import EditIcon from '@mui/icons-material/Edit'
import SettingsModal from './SettingsModal'
import { ToolTip } from '../dentalenhancements/components/ToolTip'
import { WarningAlertBox } from '../../../components/AlertBoxes.styled'
import { hour, timezones } from './constants'
import { addError, addSuccess } from '../../../redux/alerts'
import { getUserDetails } from '../../../services/userService'
import useCustomerLoading from '../../../hooks/useCustomerLoading'

const Settings = () => {
  const theme = useTheme()

  const groups = useAppSelector(selectGroups)
  const customer = useAppSelector(selectCustomer)
  const customerNumber = useAppSelector(selectCustomerNumber)
  const currentUser = useAppSelector(selectCurrentUser)
  const isAdminOrOwner = useAppSelector(selectIsAdminOrOwner)
  const teamMembers = useAppSelector(selectTeamMembers)
  const loadingState = useCustomerLoading()

  const [waterlineNotificationGroup, setWaterlineNotificationGroup] = useState<GroupName[]>([])
  const [biologicalNotificaionGroup, setBiologicalNotificationGroup] = useState<GroupName[]>([])
  const [defaultCustomer, setDefaultCustomer] = useState<CustomerDetails>({} as CustomerDetails)
  const [open, setOpen] = useState(false)
  const [timezoneLabel, setTimezoneLabel] = useState('')
  const [reminderTime, setReminderTime] = useState('')
  const [user, setUser] = useState<User | null>(null)
  const dispatch = useAppDispatch()

  const fontStyles = {
    fontSize: '13px',
    fontFamily: "'Open Sans', sans-serif"
  }
  const StyledText = styled(Typography)(() => fontStyles)
  const AccountDataText = styled(Typography)(() => ({ ...fontStyles, color: theme.palette.color50555b.main, width: '250px', marginBottom: '10px' }))

  const setNotificationGroups = (sourceUser: User | null = user) => {
    if (!sourceUser) {
      return
    }

    try {
      const waterlineGroups: { [Identifier: string]: number } = {}
      const biologicalGroups: { [Identifier: string]: number } = {}

      const notificationGroups = {
        waterlineLine: [] as GroupName[],
        biological: [] as GroupName[]
      }

      const userNotificationGroups = Array.isArray(sourceUser.user_notification_groups)
        ? sourceUser.user_notification_groups
        : []

      groups.forEach((group: GroupName) => {
        userNotificationGroups.forEach((userGroup) => {
          const groupId = Number(userGroup.group_id)

          if (userGroup.category === 'waterline' && !waterlineGroups[group.group_id] && groupId === Number(group.group_id)) {
            notificationGroups.waterlineLine.push(group)
            waterlineGroups[group.group_id] = 1
          }

          if (userGroup.category === 'biological' && !biologicalGroups[group.group_id] && groupId === Number(group.group_id)) {
            notificationGroups.biological.push(group)
            biologicalGroups[group.group_id] = 1
          }
        })
      })

      setWaterlineNotificationGroup(notificationGroups.waterlineLine)
      setBiologicalNotificationGroup(notificationGroups.biological)
    } catch (error) {
      console.error(error)
      dispatch(addError(error instanceof Error ? error.message : 'There was an error getting your customer information'))
    }
  }

  const getUser = async (): Promise<User | null> => {
    try {
      const userDetails = await getUserDetails(customerNumber)
      const currentUserDetails = teamMembers.find(({ login_user_id, customer_number }) => String(login_user_id) === String(currentUser?.login_user_id) && customer_number === customerNumber)

      const mergedUser = {
        ...currentUser,
        ...currentUserDetails,
        ...userDetails,
        user_notification_groups: userDetails.user_notification_groups ?? []
      } as User

      setUser(mergedUser)
      setReminderTime(hour.find((h) => h.value === userDetails.reminder_time)?.label ?? '')
      setTimezoneLabel(timezones.find((tz) => tz.value === userDetails.timezone)?.label ?? '')
      setDefaultCustomer(
        currentUser?.available_customers?.find(
          (availableCustomer: CustomerDetails) => availableCustomer.customer_number === userDetails?.default_customer_number
        ) as CustomerDetails
      )

      return mergedUser
    } catch (error) {
      console.error(error)
      dispatch(addError(error instanceof Error ? error.message : 'There was an error getting your user information'))
      return null
    }
  }

  useEffect(() => {
    if (user && customerNumber) {
      setNotificationGroups(user)
    }
  }, [customerNumber, groups, user])

  useEffect(() => {
    if (!customerNumber || !currentUser?.key_) {
      return
    }
    if (loadingState !== LoadingState.Done) {
      return
    }

    getUser()
  }, [customerNumber, currentUser?.key_, currentUser?.login_user_id, loadingState])

  const handleOpenModal = () => setOpen(true)

  const handleClose = async (success = false) => {
    setOpen(false)

    if (success) {
      await getUser()
      dispatch(getCustomerMetadata(customerNumber))
      dispatch(addSuccess('Successfully updated settings.'))
    }
  }

  const waterlineComplianceInterval = customer.customerMetadata
    ? customer.customerMetadata.waterline_compliance_interval.toLocaleLowerCase() === 'quarter' ? 'Quarterly' : 'Annually'
    : ''

  return (
    <Box sx={{ color: theme.palette.color50555b.main }}>
      <Box sx={{ position: 'fixed', width: '100%', zIndex: 1000 }}>
        <PageHeader headerName={'Account Settings'} size={'26px'} />
        <Button
          sx={{ position: 'fixed', top: '59px', right: '16px' }}
          variant="contained"
          component="label"
          onClick={handleOpenModal}
          startIcon={<EditIcon />}
        >
          Edit
        </Button>
      </Box>
      <Box sx={{ pt: '66px', color: theme.palette.color50555b.main }}>
        <Box sx={{ padding: '15px' }}>
          <div className="m-b-16">
            <Card variant="outlined">
              <div className="box-header flex flex-space-between flex-align-center">Employee Information</div>
              <div style={{ padding: '16px' }}>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Name:</AccountDataText>
                  <AccountDataText>
                    {user?.firstname} {user?.lastname}
                  </AccountDataText>
                </div>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Job Title:</AccountDataText>
                  <AccountDataText>{user?.position}</AccountDataText>
                </div>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Phone Number:</AccountDataText>
                  <AccountDataText>{user?.cellphone}</AccountDataText>
                </div>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Email Address:</AccountDataText>
                  <AccountDataText>{user?.email}</AccountDataText>
                </div>
              </div>
            </Card>
          </div>

          <div className="m-b-16">
            <Card variant="outlined">
              <div className="box-header flex flex-space-between flex-align-center">Practice Information</div>
              <div style={{ padding: '16px' }}>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Default Customer:</AccountDataText>
                  <AccountDataText>{defaultCustomer.name}</AccountDataText>
                </div>
                <StyledText sx={{ marginBottom: '20px' }}>This is the practice which will be loaded by default when you log in.</StyledText>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Practice Name:</AccountDataText>
                  <AccountDataText>{customer.name}</AccountDataText>
                </div>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Practice Address:</AccountDataText>
                  <AccountDataText>
                    {customer.street} {customer.city}, {customer.state} {customer.zip}
                  </AccountDataText>
                </div>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Phone Number:</AccountDataText>
                  <AccountDataText>{customer.phone}</AccountDataText>
                </div>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Fax Number:</AccountDataText>
                  <AccountDataText>{customer.fax}</AccountDataText>
                </div>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Waterline Compliance Interval:</AccountDataText>
                  <AccountDataText>
                    {waterlineComplianceInterval}
                  </AccountDataText>
                </div>
                <div>
                  <StyledText>
                    * Email updates only apply to your online account. Call (800) 331-1984 to change your primary account email address.
                  </StyledText>
                  <StyledText>** Only the groups you are assigned to can be selected for notifications.</StyledText>
                </div>
              </div>
            </Card>
          </div>

          <div className="m-b-16">
            <Card variant="outlined">
              <div className="box-header flex flex-space-between flex-align-center">General Notification Preferences</div>
              <div style={{ padding: '16px' }}>
                <div>
                  <StyledText sx={{ display: 'block' }}>
                    OnTraq sends you reminders and notifications about things happening in your practice. Please tell us where you'd like to receive
                    these messages:
                  </StyledText>
                  {!!!user?.email_reminder && (
                    <WarningAlertBox sx={{ ...fontStyles, marginTop: '10px' }}>
                      Warning: Disabling a communication method may cause you to miss important notifications about tasks or other items pertaining to
                      your practice.
                    </WarningAlertBox>
                  )}
                  <FormGroup sx={{ padding: '7px' }}>
                    <FormControlLabel
                      disabled
                      control={<Checkbox sx={{ mr: 1 }} checked={!!user?.email_reminder} />}
                      label={<StyledText>Send via email address</StyledText>}
                    />
                  </FormGroup>
                </div>
              </div>
            </Card>
          </div>

          <div className="m-b-16">
            <Card variant="outlined">
              <div className="box-header flex flex-align-center">
                <span className='m-r-8'>Biological Notification Preferences</span>
                <ToolTip helpText="As per your company policy, this setting is managed by the Owner/Admin" />
              </div>
              <div style={{ padding: '16px' }}>
                <div>
                  <FormGroup sx={{ padding: '7px' }}>
                    <FormControlLabel
                      sx={{ mb: 1 }}
                      disabled
                      control={<Checkbox sx={{ mr: 1 }} checked={!!user?.email_bio_passed} />}
                      label={
                        <StyledText>
                          Send me notifications when my practice receives a passed Biological test result
                        </StyledText>
                      }
                    />
                    <FormControlLabel
                      sx={{ mb: 1 }}
                      disabled
                      control={<Checkbox sx={{ mr: 1 }} checked={!!user?.email_bio_failed} />}
                      label={<StyledText>Send me notifications when my practice receives a failed Biological test result</StyledText>}
                    />
                    {isAdminOrOwner && customer.corporate_parent && (
                      <FormControlLabel
                        sx={{ mb: 1 }}
                        disabled
                        control={<Checkbox sx={{ mr: 1 }} checked={!!user?.email_bio_child_failed} />}
                        label={
                          <StyledText>
                            Send me notifications when one of the practices in my organization receives a failed Biological test result
                          </StyledText>
                        }
                      />
                    )}
                  </FormGroup>
                  {isAdminOrOwner && customer.corporate_parent && !!user?.email_bio_child_failed && (
                    <div>
                      <AccountDataText sx={{ fontWeight: 'bold' }}>Biological Notification Groups</AccountDataText>
                      {biologicalNotificaionGroup.length ? (
                        <Box sx={{ display: 'flex' }}>
                          {biologicalNotificaionGroup.map((group) => (
                            <Chip sx={{ mr: 1 }} key={group.name} label={group.name} />
                          ))}
                        </Box>
                      ) : (
                        <StyledText>None Selected</StyledText>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>

          <div className="m-b-16">
            <Card variant="outlined">
              <div className="box-header flex flex-align-center">
                <span className='m-r-8'>Waterline Notification Preferences</span>
                <ToolTip helpText="As per your company policy, this setting is managed by the Owner/Admin" />
              </div>
              <div style={{ padding: '16px' }}>
                <div>
                  <FormGroup sx={{ padding: '7px' }}>
                    <FormControlLabel
                      sx={{ mb: 1 }}
                      disabled
                      control={<Checkbox sx={{ mr: 1 }} checked={!!user?.email_waterline_passed} />}
                      label={
                        <StyledText>
                          Send me notifications when my practice receives a passed Waterline test result
                        </StyledText>
                      }
                    />
                    <FormControlLabel
                      sx={{ mb: 1 }}
                      disabled
                      control={<Checkbox sx={{ mr: 1 }} checked={!!user?.email_waterline_failed} />}
                      label={<StyledText>Send me notifications when my practice receives a failed Waterline test result</StyledText>}
                    />
                    {isAdminOrOwner && customer.corporate_parent && (
                      <FormControlLabel
                        sx={{ mb: 1 }}
                        disabled
                        control={<Checkbox sx={{ mr: 1 }} checked={!!user?.email_waterline_child_failed} />}
                        label={
                          <StyledText>
                            Send me notifications when one of the practices in my organization receives a failed Waterline test result
                          </StyledText>
                        }
                      />
                    )}
                  </FormGroup>
                  {isAdminOrOwner && !!user?.email_waterline_child_failed && customer.corporate_parent && (
                    <div>
                      <AccountDataText sx={{ fontWeight: 'bold' }}>Waterline Notification Groups</AccountDataText>
                      {waterlineNotificationGroup.length ? (
                        <Box sx={{ display: 'flex' }}>
                          {waterlineNotificationGroup.map((group) => (
                            <Chip sx={{ mr: 1 }} key={group.name} label={group.name} />
                          ))}
                        </Box>
                      ) : (
                        <StyledText>None Selected</StyledText>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>

          <div className="m-b-16">
            <Card variant="outlined">
              <div className="box-header flex flex-space-between flex-align-center">Reminder and Time Zone Preferences</div>
              <div style={{ padding: '16px' }}>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Time Zone:</AccountDataText>
                  <AccountDataText>{timezoneLabel}</AccountDataText>
                </div>
                <div className="flex">
                  <AccountDataText sx={{ fontWeight: 'bold' }}>Send my reminders at (Time):</AccountDataText>
                  <AccountDataText>{reminderTime}</AccountDataText>
                </div>
              </div>
            </Card>
          </div>
        </Box>
      </Box>
      {user && (
        <SettingsModal
          user={user}
          defaultCustomer={defaultCustomer ?? ({} as CustomerDetails)}
          open={open}
          handleClose={handleClose}
          biologicalNotificationGroups={biologicalNotificaionGroup}
          waterlineNotificationGroups={waterlineNotificationGroup}
          customer={customer}
        />
      )}
    </Box>
  )
}

export default Settings
